import {useCallback, useRef, useState} from 'react';
import {SearchInput} from '../search/SearchInput';
import {OrganizerSearchItem} from '../search/OrganizerSearchItem';
import {apiRequest} from '../../common/services/api';
import {OrganizerDto} from '../../generated/models/OrganizerDto';
import {organizerSearchEndpoint} from '../../common/constants/endpoints';
import {useOrganizerStore} from '../../common/stores/useOrganizerStore';
import {useNavigate, useLocation} from 'react-router-dom';
import routes from '../routes/routes';

export const OrganizerSelectionScreen = () => {
  const [organizers, setOrganizers] = useState<OrganizerDto[] | null>();
  const [isLoading, setIsLoading] = useState(false);
  const {setOrganizer} = useOrganizerStore();
  const navigate = useNavigate();
  const location = useLocation();
  // Only the latest search may fill the list: an older, slower answer would
  // otherwise replace the results for what is typed now.
  const latestSearch = useRef(0);

  const handleSearch = useCallback(async (term: string) => {
    const trimmedValue = term.trim();

    // Prevent searching if the term is empty
    const search = ++latestSearch.current;

    if (!trimmedValue) {
      setOrganizers(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiRequest<OrganizerDto[]>({
        endpoint: organizerSearchEndpoint(trimmedValue),
        method: 'GET',
      });

      if (search !== latestSearch.current) return;
      const data = (response?.data as any)?.value;
      setOrganizers(data ? (data as OrganizerDto[]) : []);
    } catch (error) {
      if (search === latestSearch.current) setOrganizers([]);
    } finally {
      if (search === latestSearch.current) setIsLoading(false);
    }
  }, []);

  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        minHeight: '100vh',
        padding: '60px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
      }}>
      <h1
        style={{
          color: '#fff',
          marginBottom: location.state?.next ? '12px' : '40px',
        }}>
        Organizers
      </h1>
      {location.state?.next && (
        <p style={{color: '#999', margin: '0 0 28px', textAlign: 'center'}}>
          {location.state.next === routes.createPlace
            ? 'Pick the organizer the new place belongs to.'
            : 'Pick the organizer the new event belongs to.'}
        </p>
      )}

      {/* Container for Search + Loading Bar */}
      <div style={{width: '100%', maxWidth: '600px', position: 'relative'}}>
        <SearchInput
          placeholder="Type to search organizers..."
          onSearch={handleSearch}
        />

        {/* Loading Indicator */}
        <div
          style={{
            height: '3px',
            width: '100%',
            backgroundColor: 'transparent',
            marginTop: '4px',
            overflow: 'hidden',
            borderRadius: '2px',
          }}>
          {isLoading && (
            <div
              style={{
                height: '100%',
                backgroundColor: '#daff00', // Your brand lime green
                animation: 'loading-slide 1.5s infinite ease-in-out',
                width: '30%',
                borderRadius: '2px',
              }}
            />
          )}
        </div>
      </div>

      {/* CSS Animation for the sliding bar */}
      <style>{`
        @keyframes loading-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>

      {/* List of Results */}
      <div
        style={{
          marginTop: '40px',
          width: '100%',
          maxWidth: '600px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}>
        {!isLoading && organizers?.length === 0 && (
          <p style={{color: '#666', textAlign: 'center'}}>No results found</p>
        )}

        {organizers?.map(org => (
          <OrganizerSearchItem
            key={org.id}
            name={org.name}
            description={org.companyName}
            onSelect={() => {
              setOrganizer(org);
              // Screens that need an organizer first send the user here with
              // the route to continue to.
              navigate(location.state?.next ?? routes.getAllEvents, {
                state: {returnTo: location.state?.returnTo},
              });
            }}
          />
        ))}
      </div>
    </div>
  );
};
